import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Button, Chip, IconButton, Paper, Stack, Tooltip,
  CircularProgress, TextField, MenuItem, FormControlLabel, Switch, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import {
  Refresh, PhoneAndroid, Visibility, VisibilityOff, ContentCopy, FiberManualRecord,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

interface SipDevice {
  id: string;
  tenant_id: string | null;
  tenant_name: string | null;
  username: string;
  password: string;
  domain: string;
  caller_name: string | null;
  caller_number: string | null;
  concurrent_limit: number;
  max_contacts: number;
  status: string;
  created_at: string;
  registered: boolean;
  contact_addr: string | null;
  contact_expires: string | null;
  user_agent: string | null;
}

// The SBC endpoint customers point their device/PBX at (digest-auth, dynamic IP).
const SBC_HOST = "esbc2.synergytalk.org";
const SBC_PORT = 5080;
const SBC_TRANSPORT = "TLS";

export const SipDevicesPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [rows, setRows] = useState<SipDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [auto, setAuto] = useState(true);
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  // filters
  const [customer, setCustomer] = useState("");
  const [reg, setReg] = useState("");        // "", "online", "offline"
  const [search, setSearch] = useState("");

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setReloading(true);
    try {
      const { data } = await api.get("/tenants/sip-devices/all");
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      if (!quiet) enqueueSnackbar(e?.response?.data?.message || "Failed to load SIP devices", { variant: "error" });
    } finally {
      setLoading(false); setReloading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [auto, load]);

  const customers = useMemo(
    () => Array.from(new Set(rows.map(r => r.tenant_name).filter(Boolean))).sort() as string[],
    [rows],
  );

  const filtered = useMemo(() => rows.filter(r => {
    if (customer && r.tenant_name !== customer) return false;
    if (reg === "online" && !r.registered) return false;
    if (reg === "offline" && r.registered) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${r.username} ${r.tenant_name || ""} ${r.caller_name || ""} ${r.caller_number || ""} ${r.contact_addr || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [rows, customer, reg, search]);

  const online = rows.filter(r => r.registered).length;

  const copy = (t: string) => { navigator.clipboard?.writeText(t); enqueueSnackbar("Copied", { variant: "success" }); };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <PhoneAndroid color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1 }}>SIP Devices</Typography>
        <Chip size="small" label={`${rows.length} total`} />
        <Chip size="small" color="success" variant="outlined" label={`${online} online`} />
        <Chip size="small" color="default" variant="outlined" label={`${rows.length - online} offline`} />
        <FormControlLabel
          control={<Switch size="small" checked={auto} onChange={e => setAuto(e.target.checked)} />}
          label={<Typography variant="caption">Auto</Typography>}
        />
        <Button size="small" startIcon={reloading ? <CircularProgress size={14} /> : <Refresh />} onClick={() => load()}>
          Refresh
        </Button>
      </Stack>

      <Alert severity="info" icon={<PhoneAndroid fontSize="inherit" />} sx={{ mb: 2 }}>
        Registered SIP accounts (digest auth, dynamic IP). Devices point their PBX/softphone at{" "}
        <b>{SBC_HOST}:{SBC_PORT}</b> over <b>{SBC_TRANSPORT}</b>, registering with the username/password below —
        no IP trunk or ACL required. Each account shares its customer's rate plan, CLI policy and DID pool.
      </Alert>

      {/* filter bar (flex, not MUI Grid — avoids the v9 item-props pitfall) */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField select size="small" label="Customer" value={customer} onChange={e => setCustomer(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="">All customers</MenuItem>
            {customers.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Registration" value={reg} onChange={e => setReg(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
          </TextField>
          <TextField size="small" label="Search" placeholder="username, customer, caller, IP…" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ minWidth: 260, flexGrow: 1 }} />
          {(customer || reg || search) && (
            <Button size="small" onClick={() => { setCustomer(""); setReg(""); setSearch(""); }}>Clear</Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Registration</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Password</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Caller ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contact / UA</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Conc.</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={26} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>No SIP devices found</TableCell></TableRow>
            ) : filtered.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Tooltip title={r.registered
                    ? `Registered${r.contact_expires ? ` · expires in ${r.contact_expires}s` : ""}`
                    : "Not registered"}>
                    <Chip
                      size="small"
                      icon={<FiberManualRecord sx={{ fontSize: "0.7rem !important" }} />}
                      color={r.registered ? "success" : "default"}
                      variant={r.registered ? "filled" : "outlined"}
                      label={r.registered ? "Online" : "Offline"}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {r.tenant_name
                    ? <Button size="small" sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                        onClick={() => r.tenant_id && navigate(`/customers/${r.tenant_id}`)}>{r.tenant_name}</Button>
                    : <em style={{ color: "#999" }}>unassigned</em>}
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <code style={{ fontSize: "0.85rem" }}>{r.username}</code>
                    <IconButton size="small" onClick={() => copy(r.username)}><ContentCopy sx={{ fontSize: "0.85rem" }} /></IconButton>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <code style={{ fontSize: "0.85rem" }}>{reveal[r.id] ? r.password : "••••••••"}</code>
                    <IconButton size="small" onClick={() => setReveal(s => ({ ...s, [r.id]: !s[r.id] }))}>
                      {reveal[r.id] ? <VisibilityOff sx={{ fontSize: "0.9rem" }} /> : <Visibility sx={{ fontSize: "0.9rem" }} />}
                    </IconButton>
                    {reveal[r.id] && <IconButton size="small" onClick={() => copy(r.password)}><ContentCopy sx={{ fontSize: "0.85rem" }} /></IconButton>}
                  </Stack>
                </TableCell>
                <TableCell>
                  {r.caller_name || <span style={{ color: "#aaa" }}>—</span>}
                  {r.caller_number ? <Typography variant="caption" display="block" color="text.secondary">{r.caller_number}</Typography> : null}
                </TableCell>
                <TableCell>
                  {r.registered
                    ? <Box>
                        <Typography variant="caption" display="block">{r.contact_addr}</Typography>
                        {r.user_agent && <Typography variant="caption" color="text.secondary">{r.user_agent}</Typography>}
                      </Box>
                    : <span style={{ color: "#aaa" }}>—</span>}
                </TableCell>
                <TableCell align="center">{r.concurrent_limit}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined"
                    color={r.status === "active" ? "primary" : "default"}
                    label={r.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
